/**
 * Protected route wrapper.
 *
 * Sends unauthenticated users straight to brewauth (there is no local
 * /login form to bounce through anymore — brewauth owns the sign-in UI).
 * Optionally checks for required roles (e.g., 'admin' for admin routes).
 * Mirrors the legacy AngularJS requireToken + role checks on route definitions.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '@/auth/useAuth';
import { useAppInfo } from '@/app/useAppInfo';
import { login, loginFresh, autoLogin } from '@/auth/reauthClient';
import { SIGN_IN_UNREACHABLE_MESSAGE, useSignInRedirect } from '@/auth/useSignInRedirect';
import SessionUnavailablePanel from './SessionUnavailablePanel';
import AppInfoUnavailablePanel from './AppInfoUnavailablePanel';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Optional roles required to access this route */
  requiredRoles?: string[];
}

export default function ProtectedRoute({ children, requiredRoles }: Readonly<ProtectedRouteProps>) {
  const { authenticated, loading: authLoading, hasRole, remotelySignedOut, localSignout, unavailable, refreshProfile } = useAuth();
  const { info, loading: infoLoading, unauthorized: infoUnauthorized } = useAppInfo();

  const rolesNeeded = !!requiredRoles?.length;
  // autoLogin() refused (3 per 120 s): stop bouncing and let the user decide.
  const [loginBounced, setLoginBounced] = useState(false);
  // A sign-in redirect that could not reach brewauth (#2371).
  const { unreachable, signIn, retry, report } = useSignInRedirect();

  // Not authenticated (and auth state has settled): remember where we are so
  // the post-login callback can return here, then hand off to brewauth.
  // Suppressed when another tab just signed out — see the remotelySignedOut
  // branch below for why auto-login must not fire in that case. Also
  // suppressed when the backend is unavailable: that is not a signed-out
  // answer, and logging in again only mints sessions (jorigin/brewauth#139).
  // And suppressed after this tab's own logout failed to reach brewauth: the
  // SSO session may still be live, so auto-login would undo the logout (#2192).
  useEffect(() => {
    if (!authLoading && !authenticated && !remotelySignedOut && !localSignout && !unavailable) {
      sessionStorage.setItem('postLoginHash', window.location.hash);
      // Deferred: the outcome of an external side effect (navigation or
      // refusal) is reported back to React from a callback, not the effect body.
      if (!autoLogin(() => report(login))) queueMicrotask(() => setLoginBounced(true));
    }
  }, [authLoading, authenticated, remotelySignedOut, localSignout, unavailable, report]);

  // The sign-in redirect could not start: say so, rather than a spinner
  // that never ends or a Sign in button that does nothing.
  if (!authLoading && !authenticated && unreachable) {
    return (
      <SessionUnavailablePanel
        message={SIGN_IN_UNREACHABLE_MESSAGE}
        actionLabel="Retry"
        onAction={retry}
      />
    );
  }

  // Another tab signed out: the SSO cookie may still be live for a moment
  // while that tab's end_session request is in flight. Auto-login here would
  // race it and could silently re-log this tab back in. Show an explicit
  // sign-in prompt instead — by the time the user clicks it, the SSO session
  // has been revoked, so brewauth shows its login page.
  if (!authLoading && remotelySignedOut) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="cell-muted">You've been signed out in another tab.</p>
        <button
          type="button"
          className="app-btn app-btn-primary"
          onClick={() => {
            sessionStorage.setItem('postLoginHash', window.location.hash);
            signIn(login);
          }}
        >
          Sign in
        </button>
      </div>
    );
  }

  // This tab logged out but brewauth's end_session could not run (#2192).
  // Sign in with prompt=login, so a still-live SSO session cannot carry the
  // user straight back in.
  if (!authLoading && localSignout) {
    return (
      <SessionUnavailablePanel
        message="You're signed out."
        actionLabel="Sign in"
        onAction={() => signIn(loginFresh)}
      />
    );
  }

  if (!authLoading && !authenticated && unavailable) {
    return (
      <SessionUnavailablePanel
        message="We can't reach brewlytics right now."
        actionLabel="Retry"
        onAction={() => void refreshProfile()}
      />
    );
  }

  if (!authLoading && !authenticated && loginBounced) {
    return (
      <SessionUnavailablePanel
        message="Sign-in keeps bouncing back. Your work is safe."
        actionLabel="Sign in"
        onAction={() => {
          sessionStorage.setItem('postLoginHash', window.location.hash);
          signIn(login);
        }}
      />
    );
  }

  // No signed-in page renders without /api/info: its classification banner
  // and its role checks read it. It did not load: say so, and keep trying
  // (#2392, #2399). Not for a 401: the auth flow owns that answer.
  if (!authLoading && authenticated && !infoLoading && !info && !infoUnauthorized) {
    return <AppInfoUnavailablePanel />;
  }

  // hasRole() reads info.roles from AppInfoContext. /api/profiles/self and
  // /api/info race on cold boot — if the profile lands first we'll see
  // authenticated=true while info is still null and every hasRole() check
  // returns false, bouncing the user to /403. Hold the spinner until both
  // contexts are settled, so no page renders without its banner either
  // (#2399). Also hold it while an unauthenticated user is being redirected
  // to brewauth above.
  if (authLoading || !authenticated || infoLoading || !info) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full size-8 border-b-2 border-brew-blue" />
      </div>
    );
  }

  // Check required roles
  if (rolesNeeded) {
    const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
    if (!hasRequiredRole) {
      return <Navigate to="/403" replace />;
    }
  }

  return <>{children}</>;
}
